// src/components/Template/SaveTemplateDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { createTemplate, activateTemplate, TemplateApiError, CreateTemplateRequest } from '../../services/templateApi';

interface SaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  onSuccess?: (templateId: number) => void;
}

const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  open,
  onClose,
  projectId,
  onSuccess,
}) => {
  const [templateName, setTemplateName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateName('');
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const request: CreateTemplateRequest = {
        projectId,
        templateName: templateName.trim(),
      };
      
      const response = await createTemplate(request);
      
      // Automatically activate (publish) the template
      try {
        await activateTemplate(response.templateId);
      } catch (activateErr) {
        // Log but don't fail - template was created, just not activated
        console.warn('Template created but failed to activate:', activateErr);
      }
      
      if (onSuccess) {
        onSuccess(response.templateId);
      }
      
      onClose();
    } catch (err) {
      const apiError = err as TemplateApiError;
      let errorMessage = apiError.message || 'Failed to save template';
      
      // Provide more specific error messages based on error code
      if (apiError.code === 'NETWORK_ERROR') {
        if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the server. Please check your connection and ensure the backend is running.';
        }
      } else if (apiError.code === 'NOT_FOUND') {
        errorMessage = 'The project could not be found or may not be available for template creation.';
      } else if (apiError.code === 'VALIDATION_ERROR') {
        errorMessage = apiError.message || 'Validation failed. Please check your input.';
      } else if (apiError.code === 'FORBIDDEN') {
        errorMessage = 'You do not have permission to create templates.';
      } else if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      }
      
      setError(errorMessage);
      console.error('Error saving template:', {
        error: err,
        apiError,
        projectId,
        templateName,
        code: apiError.code,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Project as Template</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={isSaving}
            helperText="Enter a name for this template"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isSaving && templateName.trim()) {
                handleSave();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !templateName.trim()}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveTemplateDialog;

